import { useState, useEffect, useRef, useCallback } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/zh-cn';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import localeData from 'dayjs/plugin/localeData';
import React from 'react';
import CustomTitle from './CustomTitle';
import CustomInput from './CustomInput';
import CustomParagraph from './CustomParagraph';
import CustomButton from './CustomButton';
import CustomSelect from './CustomSelect';
import { Calendar, Col, message, Row, Select } from 'antd';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import LeftIcon from '../assets/images/greyIcons/filled.png';
import RightIcon from '../assets/images/greyIcons/Icon.png';
import ArrowDown from '../assets/icons/down-filled.png';
import { ReportingPeriodList } from '../common/static_data';
import { useToken } from '@/common/helper';

dayjs.extend(isSameOrAfter);
dayjs.extend(localeData);
dayjs.extend(utc);
dayjs.extend(timezone);

// Slash-separated numeric input regex per date_format preference — the
// digit grouping (2-2-4 vs 4-2-2) is handled separately in handleDatefromInputs.
const DATE_INPUT_REGEX: Record<string, RegExp> = {
  'MM/DD/YYYY': /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/,
  'DD/MM/YYYY': /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/,
  'YYYY/MM/DD': /^\d{4}\/(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])$/,
};

interface DateRange {
  start?: string;
  end?: string;
  period?: string;
}

interface ChildPayload {
  oldId?: string;
  newId?: string;
  dateFilter: string;
  startDate: string;
  endDate: string;
}

interface DateRangeValue {
  oldId?: string;
  newId?: string;
  dateFilter: string;
  rangeStart: string;
  rangeEnd: string;
  period?: string;
}

// Union type for the function parameter
type Payload = ChildPayload | DateRangeValue;

interface CustomizeFilterCalendarProps {
  handleDataFromChild: (data: Payload) => void;
  editingId?: string;
  dateFilter: string;
  showReportingPeriod?: boolean;
}

const CustomizeFilterCalendar: React.FC<CustomizeFilterCalendarProps> = ({
  handleDataFromChild,
  editingId,
  dateFilter,
  showReportingPeriod = true,
}) => {
  const { decodedUserToken } = useToken();
  const user = decodedUserToken();
  const dateFormat = user?.date_format || 'MM/DD/YYYY';
  const [rangeStart, setRangeStart] = useState<string | null>(null);
  const [rangeEnd, setRangeEnd] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [period, setPeriod] = useState<string>('Custom');
  const buttonPress = useRef(false);

  const inferPeriodFromDates = useCallback(
    (start?: string, end?: string): string | undefined => {
      if (!start || !end) return undefined;
      const tz = user?.user_time_zone || dayjs.tz.guess();
      const now = dayjs().tz(tz);
      const s = dayjs(start);
      const e = dayjs(end);
      const todayStr = now.format('YYYY-MM-DD');
      const yesterdayStr = now.subtract(1, 'day').format('YYYY-MM-DD');

      if (
        s.format('YYYY-MM-DD') === todayStr &&
        e.format('YYYY-MM-DD') === todayStr
      )
        return 'Today';
      if (
        s.format('YYYY-MM-DD') === yesterdayStr &&
        e.format('YYYY-MM-DD') === yesterdayStr
      )
        return 'Yesterday';
      if (
        s.isSame(now.subtract(1, 'week').startOf('week'), 'day') &&
        e.isSame(now.subtract(1, 'week').endOf('week'), 'day')
      )
        return 'Last week';
      if (
        s.isSame(now.subtract(1, 'month').startOf('month'), 'day') &&
        e.isSame(now.subtract(1, 'month').endOf('month'), 'day')
      )
        return 'Last month';

      if (e.format('YYYY-MM-DD') === todayStr) {
        if (s.isSame(now.startOf('week'), 'day')) return 'Week to date';
        if (s.isSame(now.startOf('month'), 'day')) return 'Month to date';
        if (s.isSame(now.startOf('year'), 'day')) return 'Year to date';
        if (s.isSame(now.subtract(6, 'day'), 'day')) return 'Last 7 days';
        if (s.isSame(now.subtract(29, 'day'), 'day')) return 'Last 30 days';
        const qStartMonth = Math.floor(now.month() / 3) * 3;
        if (s.isSame(now.month(qStartMonth).startOf('month'), 'day'))
          return 'Quarter to date';
      }
      return undefined;
    },
    [user?.user_time_zone]
  );

  const formatOut = (d: Dayjs) => d.format('YYYY-MM-DD');
  const handlePeriodChange = useCallback(
    (value: string) => {
      setPeriod(value);
      let start: string | null = null;
      let end: string | null = null;

      const tz = user?.user_time_zone || dayjs.tz.guess();
      const today = dayjs().tz(tz);
      end = today.format('YYYY-MM-DD'); // default end is today; overridden below for periods that don't end today
      if (value === 'Today') {
        start = today.format('YYYY-MM-DD');
      } else if (value === 'Yesterday') {
        start = today.subtract(1, 'day').format('YYYY-MM-DD');
        end = today.subtract(1, 'day').format('YYYY-MM-DD');
      } else if (value === 'Last week') {
        start = today.subtract(1, 'week').startOf('week').format('YYYY-MM-DD');
        end = today.subtract(1, 'week').endOf('week').format('YYYY-MM-DD');
      } else if (value === 'Last month') {
        start = today
          .subtract(1, 'month')
          .startOf('month')
          .format('YYYY-MM-DD');
        end = today.subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
      } else if (value === 'Week to date') {
        start = today.startOf('week').format('YYYY-MM-DD');
      } else if (value === 'Last 24 hours') {
        start = today.subtract(1, 'day').format('YYYY-MM-DD');
      } else if (value === 'Last 7 days') {
        start = today.subtract(6, 'day').format('YYYY-MM-DD');
      } else if (value === 'Last 30 days') {
        start = today.subtract(29, 'day').format('YYYY-MM-DD');
      } else if (value === 'Month to date') {
        start = today.startOf('month').format('YYYY-MM-DD');
      } else if (value === 'Quarter to date') {
        const qStart = Math.floor(today.month() / 3) * 3;
        start = today.month(qStart).startOf('month').format('YYYY-MM-DD');
      } else if (value === 'Year to date') {
        start = today.startOf('year').format('YYYY-MM-DD');
      } else if (value === 'All time') {
        start = null;
      } else if (value === 'Custom') {
        start = null;
        end = null;
      } else {
        const parsed = dayjs(value);
        if (parsed.isValid()) {
          start = parsed.startOf('month').format('YYYY-MM-DD');
          end = parsed.endOf('month').format('YYYY-MM-DD');
        }
      }

      setRangeStart(start);
      setRangeEnd(end);
      setSelectedDate(dayjs());
      setError(null);
    },
    [user?.user_time_zone]
  );

  useEffect(() => {
    if (!editingId) {
      setRangeStart(null);
      setRangeEnd(null);
      setSelectedDate(dayjs());
      setError(null);
      setPeriod('Custom');
      return;
    }

    try {
      const parsed: DateRange = JSON.parse(editingId);
      const { start, end, period: savedPeriod } = parsed || {};
      if (savedPeriod) {
        handlePeriodChange(savedPeriod);
        setPeriod(savedPeriod);
        setError(null);
        return;
      }

      setRangeStart(start ?? null);
      setRangeEnd(end ?? null);

      if (start) setSelectedDate(dayjs(start));
      else if (end) setSelectedDate(dayjs(end));
      else setSelectedDate(dayjs());

      setPeriod(inferPeriodFromDates(start, end) || 'Custom');
      setError(null);
    } catch {
      setRangeStart(null);
      setRangeEnd(null);
      setSelectedDate(dayjs());
      setError(null);
      setPeriod('Custom');
    }
  }, [editingId, handlePeriodChange, inferPeriodFromDates]);

  const onDateChange = (val: Dayjs) => {
    if (buttonPress.current) {
      buttonPress.current = false;
      return;
    }
    setError(null);
    setSelectedDate(val);
    if (!rangeStart) {
      setRangeStart(formatOut(val));
    } else if (!rangeEnd) {
      if (dayjs(val).isSameOrAfter(dayjs(rangeStart), 'day')) {
        setRangeEnd(formatOut(val));
      } else {
        setError('Range start must be less than range end');
      }
    } else {
      setRangeEnd(null);
      setRangeStart(formatOut(val));
    }
    setPeriod('Custom');
  };

  const sendDataToParent = () => {
    const normalized = (period || '').trim();
    const includePeriod =
      normalized.length > 0 && normalized.toLowerCase() !== 'custom';
    const idPayload = includePeriod
      ? {
          period: normalized,
          start: rangeStart,
          end: rangeEnd,
        }
      : {
          start: rangeStart,
          end: rangeEnd,
        };
    if (!rangeEnd) {
      message.error(
        'Please select a valid date range before applying the filter.'
      );
      return;
    }
    handleDataFromChild({
      oldId: editingId ?? null,
      dateFilter,
      rangeStart,
      rangeEnd,
      ...(includePeriod ? { period: normalized } : {}),
      newId: JSON.stringify(idPayload),
    });
  };

  const now = new Date();
  const dynamicMonths = Array.from({ length: 3 }).map((_, i) => {
    const date = new Date(now.getFullYear(), now.getMonth() - (i + 1), 1);
    const label = date.toLocaleString('default', {
      month: 'long',
      year: 'numeric',
    });
    return { label, value: label };
  });
  const dynamicLastmonths = [...ReportingPeriodList, ...dynamicMonths];

  const wrapperStyle = { width: 300, border: '0px', borderRadius: '0px' };

  // handle date input manually
  const handleDatefromInputs = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'start' | 'end'
  ) => {
    let input = e.target.value.replace(/\D/g, '');
    if (input.length > 8) input = input.slice(0, 8);

    // Auto-insert slashes, grouped per the date_format preference:
    // year-first is 4-2-2 (e.g. 2025/03/21), everything else is 2-2-4 (e.g. 03/21/2025)
    const isYearFirst = dateFormat.startsWith('YYYY');
    if (isYearFirst) {
      if (input.length > 6) {
        input = input.replace(/^(\d{4})(\d{2})(\d+)/, '$1/$2/$3');
      } else if (input.length > 4) {
        input = input.replace(/^(\d{4})(\d+)/, '$1/$2');
      }
    } else if (input.length > 4) {
      input = input.replace(/^(\d{2})(\d{2})(\d+)/, '$1/$2/$3');
    } else if (input.length > 2) {
      input = input.replace(/^(\d{2})(\d+)/, '$1/$2');
    }

    // Show slashes to user
    if (type === 'start') setRangeStart(input);
    else setRangeEnd(input);

    const dateRegex = DATE_INPUT_REGEX[dateFormat];

    if (dateRegex.test(input) && dayjs(input, dateFormat, true).isValid()) {
      setError(null);

      const serverFormat = dayjs(input, dateFormat).format('YYYY-MM-DD');

      if (type === 'start') setRangeStart(serverFormat);
      else setRangeEnd(serverFormat);

      // Check range logic
      const startDate = rangeStart ? dayjs(rangeStart, 'YYYY-MM-DD') : null;
      const endDate = rangeEnd ? dayjs(rangeEnd, 'YYYY-MM-DD') : null;

      if (type === 'start' && endDate && dayjs(serverFormat).isAfter(endDate)) {
        setError('Start date must be less than end date.');
      }
      if (
        type === 'end' &&
        startDate &&
        dayjs(serverFormat).isBefore(startDate)
      ) {
        setError('End date must be greater than start date.');
      }
    } else {
      setError(`Please enter a valid date in ${dateFormat} format.`);
    }
  };

  return (
    <div style={wrapperStyle}>
      <Calendar
        value={selectedDate}
        onChange={(v) => setSelectedDate(v as Dayjs)}
        fullscreen={false}
        onSelect={onDateChange}
        fullCellRender={(date) => {
          const cellDate = dayjs(date);
          const start = rangeStart ? dayjs(rangeStart) : null;
          const end = rangeEnd ? dayjs(rangeEnd) : null;
          const inRange = !!(
            start &&
            end &&
            !cellDate.isBefore(start, 'day') &&
            !cellDate.isAfter(end, 'day')
          );
          const isStart = !!(start && cellDate.isSame(start, 'day'));
          const isEnd = !!(end && cellDate.isSame(end, 'day'));
          const base: React.CSSProperties = {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            margin: '0 auto',
            borderRadius: isStart || isEnd ? 999 : 4,
            background:
              isStart || isEnd
                ? 'var(--text-primary)'
                : inRange
                  ? 'var(--light-gray)'
                  : undefined,
            color: isStart || isEnd ? 'var(--white)' : undefined,
            fontWeight: isStart || isEnd ? 600 : 400,
          };
          return <div style={base}>{cellDate.date()}</div>;
        }}
        headerRender={({ value, onChange }) => {
          const start = 0;
          const end = 12;
          const monthOptions = [];
          const months = dayjs.monthsShort();
          for (let i = start; i < end; i++) {
            monthOptions.push(
              <Select.Option key={i} value={i} className='month-item'>
                {months[i]}
              </Select.Option>
            );
          }
          const handlePrev = () => {
            buttonPress.current = true;
            onChange(value.clone().subtract(1, 'month'));
          };
          const handleNext = () => {
            buttonPress.current = true;
            onChange(value.clone().add(1, 'month'));
          };
          const year = value.year();
          const month = value.month();
          const options = [];
          for (let i = year - 10; i < year + 10; i += 1) {
            options.push(
              <Select.Option key={i} value={i} className='year-item'>
                {i}
              </Select.Option>
            );
          }
          return (
            <Row
              gutter={8}
              style={{
                paddingBottom: '16px',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Col
                span={11}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <button
                  style={{ display: 'contents', cursor: 'pointer' }}
                  type='button'
                  onClick={handlePrev}
                >
                  <img
                    className='zl-invert-on-dark'
                    width='12px'
                    src={LeftIcon}
                  />
                </button>
                <Select
                  style={{ width: '55%' }}
                  popupMatchSelectWidth={false}
                  getPopupContainer={(trigger) =>
                    trigger.parentElement as HTMLElement
                  }
                  value={month}
                  onChange={(newMonth: number) => {
                    buttonPress.current = true;
                    onChange(value.clone().month(newMonth));
                  }}
                  suffixIcon={
                    <img className='zl-invert-on-dark' src={ArrowDown} />
                  }
                  className='date-select-month'
                >
                  {monthOptions}
                </Select>
              </Col>
              <Col
                span={11}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Select
                  style={{ width: '60%' }}
                  popupMatchSelectWidth={false}
                  getPopupContainer={(trigger) =>
                    trigger.parentElement as HTMLElement
                  }
                  className='my-year-select date-select-month'
                  value={year}
                  onChange={(newYear: number) => {
                    buttonPress.current = true;
                    onChange(value.clone().year(newYear));
                  }}
                  suffixIcon={
                    <img className='zl-invert-on-dark' src={ArrowDown} />
                  }
                >
                  {options}
                </Select>
                <button
                  style={{ display: 'contents', cursor: 'pointer' }}
                  type='button'
                  onClick={handleNext}
                >
                  <img
                    className='zl-invert-on-dark'
                    width='26px'
                    src={RightIcon}
                  />
                </button>
              </Col>
            </Row>
          );
        }}
      />
      {showReportingPeriod && (
        <Row style={{ marginTop: 8, marginBottom: 16 }}>
          <Col span={24}>
            <CustomTitle
              text='Reporting period'
              color='var(--text-primary)'
              fontSize='12px'
              lineHeight='20px'
              fontWeight='600'
              marginBottom={0}
            />
            <CustomSelect
              placeholder='Choose period'
              value={period}
              onChange={handlePeriodChange}
              allowClear={false}
              lineHeight='16px'
              border='0px'
              bgColor='var(--light-gray)'
              borderRadius='4px'
              boxShadow='none'
              height='28px'
              options={dynamicLastmonths}
              suffixIcon={<img className='zl-invert-on-dark' src={ArrowDown} />}
            />
          </Col>
        </Row>
      )}

      <Row
        style={{
          margin: '0px',
          display: 'Flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Col span={11}>
          <CustomTitle
            text='Start'
            color='var(--text-primary)'
            fontSize='12px'
            lineHeight='20px'
            fontWeight='600'
            marginBottom={0}
          />
          <CustomInput
            placeholder={dateFormat.toLowerCase()}
            height='28px'
            width='100%'
            border='0px'
            bgColor='var(--light-gray)'
            textColor='var(--text-secondary)'
            borderRadius='4px'
            placeholderColor='var(--text-secondary)'
            lineHeight='38px'
            value={
              rangeStart
                ? dayjs(rangeStart, 'YYYY-MM-DD', true).isValid()
                  ? dayjs(rangeStart, 'YYYY-MM-DD').format(dateFormat) // server format to user display
                  : rangeStart // already user typed in dateFormat
                : ''
            }
            onChange={(e) => handleDatefromInputs(e, 'start')}
            onFocus={() => setPeriod('Custom')}
            // disabled
          />
        </Col>

        <Col span={11}>
          <CustomTitle
            text='End'
            color='var(--text-primary)'
            fontSize='12px'
            lineHeight='20px'
            fontWeight='600'
            marginBottom={0}
          />
          <CustomInput
            placeholder={dateFormat.toLowerCase()}
            height='28px'
            width='100%'
            border='0px'
            bgColor='var(--light-gray)'
            borderRadius='4px'
            textColor='var(--text-secondary)'
            placeholderColor='var(--text-secondary)'
            lineHeight='38px'
            value={
              rangeEnd
                ? dayjs(rangeEnd, 'YYYY-MM-DD', true).isValid()
                  ? dayjs(rangeEnd, 'YYYY-MM-DD').format(dateFormat)
                  : rangeEnd
                : ''
            }
            onChange={(e) => handleDatefromInputs(e, 'end')}
            onFocus={() => setPeriod('Custom')}
            // disabled
          />
        </Col>

        {error && (
          <Col span={24}>
            <CustomParagraph
              text={error}
              color='var(--danger)'
              fontSize='12px'
              fontWeight='400'
              lineHeight='28px'
              marginBottom={0}
            />
          </Col>
        )}

        <Col
          style={{
            marginTop: '12px',
            display: 'flex',
            justifyContent: 'end',
            width: '100%',
          }}
        >
          <CustomButton
            text='Apply'
            onClick={sendDataToParent}
            width='62px'
            height='36px'
            bgColor='#000'
            color='#fff'
            fontSize='12px'
            fontWeight='500'
            borderRadius='8px'
            isFormValid={error !== null}
            boxShadow='0px 4px 16px 0px rgba(0, 0, 0, 0.27)'
          />
        </Col>
      </Row>
    </div>
  );
};

export default CustomizeFilterCalendar;
